import { OrderDetailsAmountsDto } from '@tiendanube/common';
import {
  DRAFT_ORDER_DISCOUNT_COUPON_PREFIX,
  EDIT_ORDER_DISCOUNT_COUPON_PREFIX,
  POS_ORDER_DISCOUNT_COUPON_PREFIX,
} from '@tiendanube/common/src/domains/orders/orders/Constants';
import { getDiscountText } from 'domains/Marketing/Promotions/utils/promotionUtils';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { OrderDiscountType } from '../../OrderEditPage/components/Products/types';

export interface OrderDiscountsInterface {
  id: string;
  discountType?: OrderDiscountType;
  name: string;
  value: number;
  prefix?: string;
  appearance?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'danger'
    | 'light';
}

function useOrderDiscounts(
  amounts?: OrderDetailsAmountsDto,
  paymentMethod?: string,
): OrderDiscountsInterface[] {
  const t = useTranslationOrders();
  const symbolDiscountPrefix = t(
    'orderSummaryCard.tableAmounts.symbolDiscount',
  );

  if (!amounts) return [];

  const {
    discount,
    discountDetail,
    gatewayDiscount,
    couponDiscount,
    otherDiscounts,
    freeShippingDiscount,
  } = amounts;

  const discounts: OrderDiscountsInterface[] = [];

  if (!!discount && discount > 0) {
    discounts.push({
      id: 'discount',
      discountType: 'shipping_discount',
      name: freeShippingDiscount
        ? t('orderSummaryCard.tableAmounts.freeShippingDiscount')
        : discountDetail ?? t('orderSummaryCard.tableAmounts.discount'),
      value: discount,
      prefix: symbolDiscountPrefix,
      appearance: 'danger',
    });
  }

  if (!!discount && !freeShippingDiscount && discount < 0) {
    discounts.push({
      id: 'additionalShippingCost',
      name: t('orderSummaryCard.tableAmounts.aditionalCost'),
      value: -discount,
    });
  }

  if (!!gatewayDiscount && gatewayDiscount > 0) {
    discounts.push({
      id: 'gatewayDiscount',
      discountType: 'gateway_discount',
      name: paymentMethod
        ? t('orderSummaryCard.tableAmounts.gatewayDiscount', {
            paymentMethod,
          })
        : t('orderSummaryCard.tableAmounts.gatewayDiscountDefault'),
      value: gatewayDiscount,
      prefix: symbolDiscountPrefix,
      appearance: 'danger',
    });
  }

  if (!!otherDiscounts && otherDiscounts.length > 0) {
    otherDiscounts.forEach((otherDiscount) => {
      discounts.push({
        id: otherDiscount.name,
        discountType: 'promotional_discount',
        name: getDiscountText(otherDiscount, t),
        value: otherDiscount.amount,
        prefix: symbolDiscountPrefix,
        appearance: 'danger',
      });
    });
  }

  if (!!couponDiscount?.amount && couponDiscount.amount > 0) {
    discounts.push({
      id: 'couponDiscount',
      discountType: 'coupon_discount',
      name: getCouponName(t, couponDiscount),
      value: couponDiscount.amount,
      prefix: symbolDiscountPrefix,
      appearance: 'danger',
    });
  }

  return discounts;
}

function getCouponName(t, couponDiscount) {
  const couponDiscountPOS = couponDiscount?.codes?.some((discountCode) =>
    discountCode.startsWith(POS_ORDER_DISCOUNT_COUPON_PREFIX),
  );
  const couponDiscountDraft = couponDiscount?.codes?.some((discountCode) =>
    discountCode.includes(DRAFT_ORDER_DISCOUNT_COUPON_PREFIX),
  );
  const couponDiscountEdit = couponDiscount?.codes?.some((discountCode) =>
    discountCode.startsWith(EDIT_ORDER_DISCOUNT_COUPON_PREFIX),
  );

  if (couponDiscountPOS) {
    return t('orderSummaryCard.tableAmounts.posCouponDiscount');
  }
  if (couponDiscountEdit) {
    return t('orderSummaryCard.tableAmounts.editCouponDiscount');
  }
  const coupons = couponDiscountDraft
    ? t('orderSummaryCard.tableAmounts.couponDiscountDraft')
    : couponDiscount.codes?.join(' / ');
  return t('orderSummaryCard.tableAmounts.couponDiscount', { coupons });
}

export default useOrderDiscounts;
